import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Form, Button } from 'react-aria-components';
import { defineMessages, useIntl } from 'react-intl';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import {
  useDispatch,
  useSelector,
  type TypedUseSelectorHook,
} from 'react-redux';

import {
  getContactFormInfo,
  resetSubmitContactForm,
  submitContactForm,
} from '@redturtle/qubicaamf-theme/actions';
import type { SubmitContactFormInitialState } from '@redturtle/qubicaamf-theme/reducers';
import { useRecaptchaToken } from '@redturtle/qubicaamf-theme/helpers/hooks/useRecaptchaToken.hook';
import config from '@plone/volto/registry';
import styles from '../styles.module.css';
import GeneralInfoSection from './GeneralInfoSection';
import PrivacySection from './PrivacySection';
import type { FormProps } from './NewInvestmentForm';

import ThankYouPage from './thankYouPage';
import { type FormData } from './thankYouPage';
import { generalInquiriesFieldsInfo } from './formFields';
import ErrorPage from './ErrorPage';
import HoneypotField from './HoneypotField';

interface RootState {
  submitContactForm: SubmitContactFormInitialState;
}

const GeneralInquiriesFormFields = ({
  arrayValue,
  handleArrayChange,
  cat,
  people_uid,
  setFormData,
}: {
  arrayValue: string[];
  handleArrayChange: (id: string, value: string[]) => void;
  cat: string | null;
  people_uid: string | null;
  setFormData: React.Dispatch<React.SetStateAction<FormData[]>>;
}) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { getRecaptchaToken } = useRecaptchaToken();

  const jobTitleOptions = [
    intl.formatMessage(messages.owner_option),
    intl.formatMessage(messages.center_manager_option),
    intl.formatMessage(messages.mechanic_option),
    intl.formatMessage(messages.other_option),
  ];

  const fieldsInfo = generalInquiriesFieldsInfo({
    jobTitleOptions,
    handleArrayChange,
    arrayValue,
  });

  const submitForm = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    let data = {
      ...Object.fromEntries(new FormData(e.currentTarget)),
      'field-products':
        arrayValue.length > 0
          ? arrayValue
          : Object.fromEntries(new FormData(e.currentTarget)).products,
      cat: cat,
      people_uid: people_uid,
    };

    const token = await getRecaptchaToken('general_inquiry');
    if (token)
      (data as Record<string, unknown>)['g-recaptcha-response'] = token;

    const formDataInfo = fieldsInfo.map((field) => {
      if (field.id !== 'products')
        return {
          id: field.id,
          value: data[field.id],
          isRequired: field.isRequired,
        };
      else
        return {
          id: 'field-products',
          value: data['field-products'],
          isRequired: field.isRequired,
        };
    });
    setFormData(formDataInfo);

    const honeypotField = config.settings.honeypotField as string;
    const honeypotValue = honeypotField
      ? (data as Record<string, unknown>)[honeypotField]
      : undefined;
    if (honeypotField) delete (data as Record<string, unknown>)[honeypotField];

    dispatch(
      submitContactForm(
        '@general-inquiries',
        data,
        honeypotField ? { [honeypotField]: honeypotValue ?? '' } : undefined,
      ),
    );
  };

  return (
    <Form className={styles.form} onSubmit={(e) => submitForm(e)}>
      <HoneypotField />
      <GeneralInfoSection fieldsInfo={fieldsInfo} />

      <PrivacySection />
      <Button type="submit" className={styles.submitButton}>
        {intl.formatMessage(messages.submit_button)}
      </Button>
    </Form>
  );
};

const GeneralInquiriesForm = (props: FormProps) => {
  const dispatch = useDispatch();
  const googleRecaptchaKey = config.settings.gRecaptchaKey as string;

  useEffect(() => {
    dispatch(getContactFormInfo());
  }, [dispatch]);

  const useTypedSelector: TypedUseSelectorHook<RootState> =
    useSelector<RootState>;

  const [arrayValue, setArrayValue] = useState<string[]>([]);
  const [formData, setFormData] = useState<FormData[]>([]);

  const handleArrayChange = (id: string, value: string[]) => {
    setArrayValue(value);
  };

  // automatically fill the products field if products_id in qs
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  // person's category if coming from qubica base contact me link
  const cat = params.get('cat');
  // person's uid if coming from qubica base contact me link
  const people_uid = params.get('people_uid');

  useEffect(() => {
    setFormData([]);
    setArrayValue([]);
    dispatch(resetSubmitContactForm());
  }, [location.pathname, dispatch]);

  const submitState = useTypedSelector((state) => state.submitContactForm);

  const submitError = submitState.error;
  const submitSuccess = !submitState.error && submitState.loaded;

  return (
    <div className={styles.formWrapper}>
      <GoogleReCaptchaProvider reCaptchaKey={googleRecaptchaKey}>
        {!!!submitError && !submitState.loading && !submitSuccess && (
          <GeneralInquiriesFormFields
            arrayValue={arrayValue}
            handleArrayChange={handleArrayChange}
            cat={cat}
            people_uid={people_uid}
            setFormData={setFormData}
          />
        )}
        {!!submitError && <ErrorPage />}
        {!!!submitError && submitSuccess && (
          <ThankYouPage form_type="general_inquiries" formData={formData} />
        )}
      </GoogleReCaptchaProvider>
    </div>
  );
};

export default GeneralInquiriesForm;
const messages = defineMessages({
  owner_option: {
    id: 'owner_option',
    defaultMessage: 'Owner',
  },
  center_manager_option: {
    id: 'center_manager_option',
    defaultMessage: 'Center manager',
  },
  mechanic_option: {
    id: 'mechanic_option',
    defaultMessage: 'Mechanic',
  },
  other_option: {
    id: 'other_option',
    defaultMessage: 'Other',
  },
  no_value_option: {
    id: 'no_value_option',
    defaultMessage: 'Choose an option',
  },
  select_default_value: {
    id: 'select_default_value',
    defaultMessage: 'Select a value',
  },
  submit_button: {
    id: 'submit_button',
    defaultMessage: 'Submit',
  },
});
